import React, { useRef, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import { useOutletContext } from "react-router-dom";

import { apiSlice } from "../util/apiSlice";
import {
  getAllMatchups,
  getByeTeams,
  getDefaultPicks,
  getEarlyTeams,
  weekModes,
} from "../util/constants";
import ConfirmSubmitButton from "./ConfirmSubmitButton";
import ConfirmTeamModal from "./ConfirmTeamModal";
import Loading from "./Loading";
import PickTitleCard from "./PickTitleCard";
import SelectPick from "./SelectPick";
import StyledPaperCard from "./StyledPaperCard";
import SuperBowlScore from "./SuperBowlScore";
import TeamBanner from "./TeamBanner";
import TeamNameSelect from "./TeamNameSelect";
import TeamOdds from "./TeamOdds";
import { B } from "./TextStyles";

export default function Pick() {
  const { userData } = useOutletContext();

  const weekResponse = apiSlice.useGetWeekQuery();
  const week = weekResponse.data;

  const teamsStatsResponse = apiSlice.useGetTeamsStatsQuery();
  const teamsStats = teamsStatsResponse.data;

  const defaultPicks = getDefaultPicks(teamsStats, week, userData);

  const allMatchups = getAllMatchups(teamsStats, week, userData);

  const [selectedTeam, setSelectedTeam] = useState(
    userData.currentPick || null,
  );

  const [severity, setSeverity] = useState("");

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onConfirmTeamClick = () => {
    if (userData.currentPick) {
      handleOpen();
      return false;
    } else {
      return true;
    }
  };

  const handleTeamChange = (event) => {
    setSelectedTeam(event.target.value);
  };

  const makePickRef = useRef(null);
  const handleScrollToElement = () => {
    if (makePickRef.current) {
      makePickRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  const makeTeamBannerOnClick = (pick) => {
    return () => {
      setSelectedTeam(pick);
      handleScrollToElement();
    };
  };

  const [sortBySpread, setSortBySpread] = useState(false);
  const handleToggleSortBy = (event, value) => {
    if (value !== null) {
      setSortBySpread(value);
    }
  };

  if (week && week.N < 0) {
    return (
      <StyledPaperCard>
        <Stack spacing={2}>
          <Typography variant="h4" gutterBottom>
            <TeamNameSelect userData={userData} /> Pick
          </Typography>
          <Typography variant="h5" pt={2}>
            No more picks available. Come back next year for more!!
          </Typography>
        </Stack>
      </StyledPaperCard>
    );
  }

  const earlyTeams = getEarlyTeams(teamsStats);
  const isEarlyPick = earlyTeams.includes(userData.currentPick);

  if (
    week &&
    (week.mode === weekModes.LOCK_ALL ||
      (week.mode === weekModes.LOCK_THUR &&
        earlyTeams &&
        earlyTeams.includes(userData.currentPick)))
  ) {
    return (
      <Stack spacing={3} padding={3} sx={{ display: "flex" }}>
        <PickTitleCard
          userData={userData}
          week={week}
          teamsStats={teamsStats}
          isEarlyPick={isEarlyPick}
        />
      </Stack>
    );
  }

  const byeTeams = getByeTeams(teamsStats);
  let sortedMatchups;
  if (allMatchups) {
    sortedMatchups = sortBySpread
      ? allMatchups.sort((matchA, matchB) => {
          return Math.abs(matchB.home.odds) - Math.abs(matchA.home.odds);
        })
      : allMatchups.sort((matchA, matchB) => {
          return matchA.startTime.toMillis() - matchB.startTime.toMillis();
        });
  }

  return (
    <Grid container spacing={3} padding={3} alignItems="stretch">
      <Grid item xs={12} sm={12} md={12}>
        <PickTitleCard
          userData={userData}
          week={week}
          teamsStats={teamsStats}
          isEarlyPick={isEarlyPick}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} ref={makePickRef}>
        <StyledPaperCard sx={{ width: "auto" }}>
          <Stack>
            <Typography variant="h5" gutterBottom noWrap>
              MAKE YOUR PICK
            </Typography>
            <Divider sx={{ marginBottom: 2 }} />
            {!byeTeams ? (
              <Loading />
            ) : byeTeams === "error" || earlyTeams === "error" ? (
              <Alert severity="error" sx={{ width: "fit-content" }} />
            ) : (
              <>
                <SelectPick
                  userData={userData}
                  week={week}
                  byeTeams={byeTeams}
                  earlyTeams={earlyTeams}
                  onChange={handleTeamChange}
                  value={selectedTeam}
                />
                <ConfirmSubmitButton
                  data={selectedTeam}
                  postTo="/update-pick"
                  what="Pick"
                  mutationHook={apiSlice.useSetNewDataAndReloadUserDataMutation}
                  onClick={onConfirmTeamClick}
                  disabled={
                    userData.currentPick &&
                    userData.currentPick === selectedTeam
                  }
                  variant="contained"
                  severityOverride={severity}
                  sx={{ width: 280, ml: 0 }}
                />
              </>
            )}
            <ConfirmTeamModal
              onClose={handleClose}
              open={open}
              oldTeam={userData.currentPick}
              newTeam={selectedTeam}
              setSeverity={setSeverity}
            />
            {week && week.name === "Super Bowl" ? (
              <SuperBowlScore userData={userData} />
            ) : (
              ""
            )}
          </Stack>
        </StyledPaperCard>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <StyledPaperCard
          sx={{ width: "auto", minWidth: "fit-content", height: "100%" }}
        >
          <Typography variant="h5" gutterBottom>
            TEAMS NOT AVAILABLE
          </Typography>
          <Divider sx={{ marginBottom: 1 }} />
          <Typography component="div">
            {earlyTeams === "error" || byeTeams === "error" ? (
              <Alert severity="error" sx={{ width: "fit-content" }} />
            ) : (
              <>
                <B>Previously Selected:</B> {userData.usedPicks.join(", ")}
                {byeTeams && byeTeams.length > 0 ? (
                  <>
                    <br />
                    <B>Not Playing:</B> {byeTeams.join(", ")}
                  </>
                ) : (
                  ""
                )}
                {week && week.mode === weekModes.LOCK_THUR && earlyTeams ? (
                  <>
                    <br />
                    <B>Early Game{earlyTeams.length > 2 ? "s" : ""}:</B>{" "}
                    {earlyTeams.join(", ")}
                  </>
                ) : (
                  ""
                )}
              </>
            )}
          </Typography>
        </StyledPaperCard>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <StyledPaperCard>
          <Stack sx={{ gap: 2 }}>
            <Typography variant="h5">QUICK PICKS*</Typography>
            <Divider sx={{ marginBottom: 1 }} />
            {!defaultPicks ? (
              <Loading />
            ) : defaultPicks === "error" ? (
              <Alert severity="error" sx={{ width: "fit-content" }} />
            ) : (
              defaultPicks.map((team) => (
                <Stack
                  direction="row"
                  key={team}
                  sx={{ gap: 2, flexWrap: "wrap" }}
                >
                  <TeamBanner
                    team={team}
                    onClick={makeTeamBannerOnClick(team)}
                    isButton={true}
                  />
                  <TeamOdds team={team} teamsStats={teamsStats} />
                </Stack>
              ))
            )}
            <Typography pt={5} sx={{ paddingTop: 1 }}>
              *All odds are taken from fanduel, early lines.
            </Typography>
          </Stack>
        </StyledPaperCard>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <StyledPaperCard>
          <Accordion elevation={0} sx={{ border: "none" }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h5">ALL GAMES*</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <Stack gap={2}>
                <Stack direction="row" gap={2} alignItems="baseline">
                  <Typography variant="h6" gutterBottom>
                    Sort games by:
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={sortBySpread}
                    exclusive
                    onChange={handleToggleSortBy}
                  >
                    <ToggleButton value={false}>Time</ToggleButton>
                    <ToggleButton value={true}>Spread</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Divider sx={{ marginBottom: 1 }} />
                {!sortedMatchups ? (
                  <Loading />
                ) : (
                  sortedMatchups.map((match) => (
                    <Stack
                      key={match.home.pick}
                      sx={{ gap: 2, flexWrap: "wrap" }}
                    >
                      <Typography variant="h6" color="text.secondary">
                        {match.startTime.toFormat("ccc. DD @ h:mm a ZZZZ")}
                      </Typography>
                      <PickBannerForMatchUp
                        matchPick={match.away}
                        makeTeamBannerOnClick={makeTeamBannerOnClick}
                      />
                      <Divider textAlign="left" variant="inset">
                        <Typography variant="h5" color="rgba(0, 0, 0, 0.22)">
                          @
                        </Typography>
                      </Divider>
                      <PickBannerForMatchUp
                        matchPick={match.home}
                        makeTeamBannerOnClick={makeTeamBannerOnClick}
                      />
                      <Divider sx={{ borderBottomWidth: 5 }} />
                    </Stack>
                  ))
                )}
                <Typography pt={5} sx={{ paddingTop: 1 }}>
                  *All odds are taken from fanduel, early lines.
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </StyledPaperCard>
      </Grid>
    </Grid>
  );
}

function PickBannerForMatchUp({ matchPick, makeTeamBannerOnClick }) {
  return (
    <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
      <TeamBanner
        variant="h6"
        team={matchPick.pick}
        onClick={
          matchPick.available ? makeTeamBannerOnClick(matchPick.pick) : () => {}
        }
        isButton={true}
        disable={!matchPick.available}
        textPadding={1}
      />
      {matchPick.odds > 0 ? (
        ""
      ) : (
        <Typography variant="h5" align="center" pl={1} alignSelf="center">
          {`${matchPick.odds} pts`}
        </Typography>
      )}
    </Stack>
  );
}
